import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { environment } from '@env/environment';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { FormErrorMessageComponent } from '../../../shared/components/form-error-message/form-error-message.component';
import { PasswordModule } from 'primeng/password';
import * as CryptoJS from 'crypto-js';
import sha256 from 'crypto-js/sha256';
import { InputOtpChangeEvent, InputOtpModule } from 'primeng/inputotp';
import { TfaComponent } from "../tfa/tfa.component";
import { TfaType } from '@modules/settings/components/manage-users/interfaces/interfaces';

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    ButtonModule,
    InputTextModule,
    DividerModule,
    FormErrorMessageComponent,
    PasswordModule,
    InputOtpModule,
    TfaComponent
],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
})
export class LoginFormComponent {
  @Input() isSetIdpProvider!: boolean;
  @Input() returnUrl: string = '/';
  @Output() updateisForgotPasswordEvent = new EventEmitter<boolean>();
  @Output() loginSuccessEvent = new EventEmitter<void>();

  get ssoUrl(): string {
    const baseUrl = environment.protocol + '://' + environment.host + '/api/auth/sso/login';

    // Store returnUrl in sessionStorage for SSO redirect
    if (this.returnUrl && this.returnUrl !== '/') {
      sessionStorage.setItem('sso_returnUrl', this.returnUrl);
    } else {
      sessionStorage.removeItem('sso_returnUrl');
    }

    return baseUrl;
  }
  isSubmitting: boolean = false;
  loginForm: FormGroup<any> = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required]),
  });
  isLoggedIn: boolean = false;
  isTfaConfigured: boolean = true;
  tfaData: TfaType | null = null;
  tfaOtp: string = '';
  intermidateLoginBody: { email: string; password: string } | null = null;

  constructor(
    protected _auth: AuthService,
    private router: Router,
    private messageService: MessageService
  ) {}

  handleForgotPassword() {
    this.updateisForgotPasswordEvent.emit(true);
  }

  backToLoginPage() {
    this.isLoggedIn = false;
  }

  verifyOTPChange(e: InputOtpChangeEvent) {
    if (e.value.length === 6){
      this.verifyOTP();
    }
  }

  verifyOTP() {
    if (this.tfaOtp.length === 6 && this.intermidateLoginBody) {
      this.isSubmitting = true;

      let challengeBody = { email: this.intermidateLoginBody.email };

      this._auth.challenge(challengeBody).subscribe({
        next: async (res: any) => {
          let challenge = res.challenge;
          let salt = res.salt;
          const passwordHash = sha256(
            this.intermidateLoginBody?.password + salt
          ).toString(CryptoJS.enc.Hex);
          const hmac = CryptoJS.HmacSHA256(challenge, passwordHash).toString(
            CryptoJS.enc.Hex
          );

          if (hmac) {
            let loginBody = {
              email: this.intermidateLoginBody?.email,
              password: hmac,
            };

            this._auth.login({}, loginBody, this.tfaOtp).subscribe({
              next: (res: any) => {
                this.isSubmitting = false;
                this.isLoggedIn = true;
                this._auth.isLoggingIn = true;
                this.messageService.add({
                  severity: 'success',
                  summary: '',
                  detail: 'Logged in successfully',
                  key: 'tr',
                });
                this._auth.setJwtTokenAndRedirect(res.token);
                // Emit success event to parent component for proper redirection
                this.loginSuccessEvent.emit();
              },
              error: (err) => {
                this.messageService.add({
                  severity: 'warn',
                  summary: 'Warning',
                  detail: err.error.message || 'Invalid credentials',
                  key: 'tr',
                });
                this.isSubmitting = false;
                console.error(err);
              },
            });
          }
        },
        error: (err) => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Warning',
            detail: err.error.message || 'Invalid credentials',
            key: 'tr',
          });
          this.isSubmitting = false;
          console.error(err);
        },
      });
    }
  }

  updateLoginBody(): Promise<{ email: string; password: string } | null> {
    return new Promise((resolve, reject) => {
      const password = this.loginForm.value.password;
      let challengeBody = { email: this.loginForm.value.email };
      this.intermidateLoginBody = {
        email: this.loginForm.value.email,
        password: password,
      };

      this._auth.challenge(challengeBody).subscribe({
        next: (res: any) => {
          let challenge = res.challenge;
          let salt = res.salt;
          const passwordHash = sha256(password + salt).toString(CryptoJS.enc.Hex);
          const hmac = CryptoJS.HmacSHA256(challenge, passwordHash).toString(CryptoJS.enc.Hex);

          if (hmac) {
            const loginBody = {
              email: this.loginForm.value.email,
              password: hmac,
            };
            resolve(loginBody);
          } else {
            reject('HMAC generation failed');
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          reject(err);
        }
      });
    });
  }

  async onSubmit() {
    if (this.loginForm.valid) {
      this.isSubmitting = true;
      try {
        const loginBody = await this.updateLoginBody();
        if (loginBody) {
          this._auth.login({}, loginBody).subscribe({
            next: (res: any) => {
              if(!res.isTfaConfigured) {
                this.setupAndStoreTfa(res.user._id);
              }
              this.isTfaConfigured = res.isTfaConfigured;
              this.isLoggedIn = true;
              this.isSubmitting = false;
            },
            error: (err) => {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: err.error.message || 'Invalid credentials',
                key: 'tr',
              });
              this.isSubmitting = false;
              console.error(err);
            },
          });
        }
      } catch (err: any) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: err.error.message || 'Invalid credentials',
          key: 'tr',
        });
        this.isSubmitting = false;
        console.error(err);
      }
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  async login(tfaOtp: string) {
    if (this.loginForm.valid) {
      this.isSubmitting = true;
      try {
        const loginBody = await this.updateLoginBody();
        if (loginBody) {
          this._auth.login({}, loginBody, tfaOtp).subscribe({
            next: (res: any) => {
              this.isLoggedIn = true;
              this.isSubmitting = false;
              this._auth.setJwtTokenAndRedirect(res.token);
              this.messageService.add({
                severity: 'success',
                summary: '',
                detail: 'Logged in successfully',
                key: 'tr',
              });
              // Emit success event to parent component for proper redirection
              this.loginSuccessEvent.emit();
            },
            error: (err) => {
              this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: err.error.message || 'Invalid credentials',
                key: 'tr',
              });
              this.isSubmitting = false;

              if(err.error.message.includes('blocked') || err.error.message.includes('Blocked')) {
                this.backToLoginPage();
              }
            },
          });
        }
      } catch (err: any) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: err.message || 'Invalid credentials',
          key: 'tr',
        });
        this.isSubmitting = false;
        console.error(err);
      }
    }
  }

  setupAndStoreTfa(userId: string) {
    this._auth.signTFA(userId).subscribe({
      next: (tfaRes: any) => {
        this.tfaData = tfaRes.tfa;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'warn',
          summary: 'Warning',
          detail: err.error.message || 'Error in TFA setup',
          key: 'tr',
        });
        this.isSubmitting = false;
        console.error(err);
      },
    })
  }
}
